import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { LOGIN } from "@/constants/testIds/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket, MicrosoftOutlookLogo, LockKey } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { user, acceptSession, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@helpdesk.io");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const [ssoStatus, setSsoStatus] = useState(null);
  const popupRef = useRef(null);
  const popupWatchRef = useRef(null);

  useEffect(() => {
    api.get("/auth/sso-status")
      .then((r) => setSsoStatus(r.data))
      .catch(() => setSsoStatus({ sso_enabled: false }));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      window.dispatchEvent(new Event("hd:login"));
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const cleanupPopupWatch = () => {
    if (popupWatchRef.current) {
      clearInterval(popupWatchRef.current);
      popupWatchRef.current = null;
    }
  };

  const submitMicrosoft = () => {
    setLoading(true);

    const url = `${BACKEND_URL}/api/auth/sso/microsoft/login`;
    const w = 480, h = 640;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(
      url,
      "aurora_sso",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    if (!popup) {
      setLoading(false);
      toast.error("Popup blocked — please allow popups for this site and try again.", { duration: 10000 });
      return;
    }
    popupRef.current = popup;

    const handler = (event) => {
      const data = event?.data;
      if (!data || data.type !== "aurora_sso") return;
      window.removeEventListener("message", handler);
      cleanupPopupWatch();
      try { popup.close(); } catch { /* noop */ }

      const payload = data.payload || {};
      if (payload.error) {
        setLoading(false);
        toast.error(payload.error, { duration: 12000 });
        return;
      }
      if (payload.token && payload.user) {
        acceptSession(payload.token, payload.user);
        window.dispatchEvent(new Event("hd:login"));
        toast.success(`Welcome, ${payload.user?.name || "back"}`);
        navigate("/");
      } else {
        setLoading(false);
        toast.error("Microsoft sign-in returned an unexpected response");
      }
    };
    window.addEventListener("message", handler);

    // If the user closes the popup without completing, unlock the button
    popupWatchRef.current = setInterval(() => {
      if (popup.closed) {
        cleanupPopupWatch();
        window.removeEventListener("message", handler);
        setLoading(false);
      }
    }, 500);
  };

  const ssoEnabled = ssoStatus?.sso_enabled;
  const loadingStatus = ssoStatus === null;

  return (
    <div className="grain min-h-screen w-full flex items-stretch" style={{ background: "#FFFFFF" }}>
      <div className="hidden md:flex flex-col justify-between w-1/2 p-12 border-r border-slate-200 relative">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90] flex items-center justify-center">
            <Ticket size={20} weight="duotone" color="white" />
          </div>
          <span className="font-heading font-bold text-lg">Business Systems Tickets</span>
        </div>
        <div>
          <h1 className="font-heading font-bold text-4xl leading-tight tracking-tight">
            Every request,<br />in one thread.
          </h1>
          <p className="text-slate-600 mt-4 max-w-md">
            A modern Business Systems Support ticketing platform built for teams that live in email, chat, and issue trackers.
            Route, triage and resolve with AI on your side.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {[
              ["AI triage", "Auto-categorize, prioritize, draft replies"],
              ["Threaded email", "One conversation across app and Outlook"],
              ["Kanban + list", "Two views. Same source of truth."],
              ["Real inbox", "Emails become tickets automatically"],
            ].map(([t, s]) => (
              <div key={t} className="p-3 rounded-md border border-slate-200 bg-white">
                <div className="text-sm font-medium">{t}</div>
                <div className="text-xs text-slate-500 mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-500 font-mono">v0.1 · Aurora Energy</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">
              {ssoEnabled
                ? "Use your Microsoft work account to sign in."
                : "Sign in with your Business Systems Support account."}
            </p>
          </div>

          {loadingStatus ? (
            <div className="text-xs text-slate-500 font-mono">Checking sign-in options…</div>
          ) : ssoEnabled ? (
            <>
              <Button
                onClick={submitMicrosoft}
                disabled={loading}
                data-testid="login-microsoft-btn"
                className="w-full h-11 bg-[#2F2F31] hover:bg-[#141414] text-white gap-2 border border-slate-800"
              >
                <MicrosoftOutlookLogo size={18} weight="fill" />
                {loading ? "Opening Microsoft…" : "Sign in with Microsoft"}
              </Button>

              <div className="text-[11px] text-slate-500 font-mono border border-slate-200 rounded-md p-3 bg-slate-50 flex gap-2">
                <LockKey size={13} weight="duotone" className="text-[#4f4c90] shrink-0 mt-0.5" />
                <div>
                  This workspace is on <span className="font-semibold">Microsoft SSO</span>. Access is controlled by your Entra security group — ask your admin to add you.
                </div>
              </div>

              {/* Fallback password login — for admin recovery when SSO is misconfigured */}
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-900 select-none">
                  Sign in with a password instead ↓
                </summary>
                <form onSubmit={submitPassword} className="mt-3 space-y-3 border border-slate-200 rounded-md p-3 bg-slate-50">
                  <div>
                    <Label htmlFor="email-fb" className="text-[11px] text-slate-700">Email</Label>
                    <Input
                      id="email-fb"
                      type="email"
                      data-testid={LOGIN.emailInput}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 bg-white border-slate-200 h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password-fb" className="text-[11px] text-slate-700">Password</Label>
                    <Input
                      id="password-fb"
                      type="password"
                      data-testid={LOGIN.passwordInput}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 bg-white border-slate-200 h-9"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid={LOGIN.submitButton}
                    disabled={loading}
                    className="w-full h-9 bg-[#4f4c90] hover:bg-[#5F5CA5] text-white text-xs"
                  >
                    {loading ? "Signing in…" : "Sign in with password"}
                  </Button>
                  <div className="text-[10px] text-slate-500 font-mono">
                    Fallback only — please use Microsoft sign-in when possible.
                  </div>
                </form>
              </details>

              <div className="text-xs text-slate-500 text-center">
                Just want to check your ticket? <a href="/portal" className="text-[#8B87C8] hover:text-slate-900">Requester portal →</a>
              </div>
            </>
          ) : (
            <form onSubmit={submitPassword} className="space-y-6">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="email" className="text-xs text-slate-700">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid={LOGIN.emailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 bg-white border-slate-200 h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs text-slate-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid={LOGIN.passwordInput}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1 bg-white border-slate-200 h-10"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid={LOGIN.submitButton}
                disabled={loading}
                className="w-full h-10 bg-[#4f4c90] hover:bg-[#5F5CA5] text-white"
              >
                {loading ? "Signing in…" : "Sign in →"}
              </Button>
              <div className="text-xs text-slate-500 font-mono border-t border-slate-200 pt-4">
                demo: admin@helpdesk.io · admin123
              </div>
              <div className="text-xs text-slate-500 text-center">
                Just want to check your ticket? <a href="/portal" className="text-[#8B87C8] hover:text-slate-900">Requester portal →</a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
